import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AssessmentTemplate,
  AssessmentTemplateInput,
  DancerAssessment,
  DancerAssessmentInput,
} from '@pirouette/shared';
import { assessmentTemplateInputSchema, dancerAssessmentInputSchema } from '@pirouette/shared';
import type { TablesInsert } from '@pirouette/db';
import { supabase } from '@/lib/supabase';
import { stripUndefined } from '@/lib/db';

export type DancerAssessmentWithRelations = DancerAssessment & {
  dancers?: {
    id: string;
    first_name: string;
    last_name: string;
    preferred_name: string | null;
  } | null;
  assessment_templates?: Pick<AssessmentTemplate, 'id' | 'name' | 'criteria'> | null;
  classes?: { id: string; name: string } | null;
  instructors?: { id: string; first_name: string; last_name: string } | null;
};

export function useAssessmentTemplates() {
  return useQuery({
    queryKey: ['assessment-templates'],
    queryFn: async (): Promise<AssessmentTemplate[]> => {
      const { data, error } = await supabase
        .from('assessment_templates')
        .select('*')
        .order('active', { ascending: false })
        .order('name', { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as AssessmentTemplate[];
    },
  });
}

export function useCreateAssessmentTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AssessmentTemplateInput): Promise<AssessmentTemplate> => {
      const parsed = assessmentTemplateInputSchema.parse(input);
      const { data, error } = await supabase
        .from('assessment_templates')
        .insert(stripUndefined(parsed) as TablesInsert<'assessment_templates'>)
        .select('*')
        .single();
      if (error) throw error;
      return data as unknown as AssessmentTemplate;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['assessment-templates'] });
    },
  });
}

export function useDancerAssessments(dancerId?: string) {
  return useQuery({
    queryKey: ['dancer-assessments', dancerId ?? 'all'],
    queryFn: async (): Promise<DancerAssessmentWithRelations[]> => {
      let query = supabase
        .from('dancer_assessments')
        .select(
          `*,
          dancers(id, first_name, last_name, preferred_name),
          assessment_templates(id, name, criteria),
          classes(id, name),
          instructors(id, first_name, last_name)`,
        )
        .order('assessed_on', { ascending: false })
        .order('created_at', { ascending: false })
        .limit(50);

      if (dancerId) query = query.eq('dancer_id', dancerId);

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as unknown as DancerAssessmentWithRelations[];
    },
  });
}

export function useParentVisibleAssessments() {
  return useQuery({
    queryKey: ['parent-visible-assessments'],
    queryFn: async (): Promise<DancerAssessmentWithRelations[]> => {
      const { data, error } = await supabase
        .from('dancer_assessments')
        .select(
          `*,
          dancers(id, first_name, last_name, preferred_name),
          assessment_templates(id, name, criteria),
          classes(id, name),
          instructors(id, first_name, last_name)`,
        )
        .eq('parent_visible', true)
        .order('assessed_on', { ascending: false })
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as DancerAssessmentWithRelations[];
    },
  });
}

export function useCreateDancerAssessment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: DancerAssessmentInput): Promise<DancerAssessment> => {
      const parsed = dancerAssessmentInputSchema.parse(input);
      const { data, error } = await supabase
        .from('dancer_assessments')
        .insert(stripUndefined(parsed) as TablesInsert<'dancer_assessments'>)
        .select('*')
        .single();
      if (error) throw error;
      return data as DancerAssessment;
    },
    onSuccess: (_result, input) => {
      void qc.invalidateQueries({ queryKey: ['dancer-assessments'] });
      void qc.invalidateQueries({ queryKey: ['dancer-assessments', input.dancer_id] });
      void qc.invalidateQueries({ queryKey: ['parent-visible-assessments'] });
    },
  });
}
